/**
 * Admin reset password v2 flow — modal body shown inside ShareInvestigation.
 *
 * Two screens:
 *   1. Confirmation: warn the admin about the consequences (target's tokens are
 *      revoked, target must log in with a temp password, multi-investigation
 *      lockout if the target lives in other investigations as well).
 *   2. Reveal: shows the one-time temp password generated by
 *      `adminResetPasswordV2` plus a copy-to-clipboard button. The secret is
 *      never persisted on disk and never re-displayed.
 *
 * The actual crypto + API call lives in services/researcherSecurity.ts so this
 * component stays pure UI.
 */
import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Translate } from 'react-localize-redux';
import { adminResetPasswordV2 } from '../../../services/researcherSecurity';

const COPY_CLEAR_MS = 30_000;

export default function ResetPasswordFlow({ target, investigationUuid, onClose }) {
    const [acknowledged, setAcknowledged] = useState(false);
    const [stage, setStage] = useState('confirm'); // 'confirm' | 'reveal' | 'error'
    const [tempPassword, setTempPassword] = useState(null);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [copied, setCopied] = useState(false);

    // The current researcher-list endpoint doesn't tell us whether the target
    // is a member of OTHER investigations. Until that data is wired, we treat
    // every reset conservatively: surface the multi-investigation warning + ack
    // unconditionally. False positives (target only in this investigation) cost
    // one extra click; false negatives (target in many investigations) would
    // silently brick their access elsewhere, which is the real risk.
    const hasMulti = true;

    // Clear the clipboard after a fixed window to limit exposure if the admin
    // walked away from their machine. Best-effort — some browsers throw on the
    // write outside a user gesture; we swallow.
    useEffect(() => {
        if (!copied) return;
        const t = setTimeout(() => {
            try { navigator.clipboard.writeText('').catch(() => {}); } catch (_) { /* no-op */ }
            setCopied(false);
        }, COPY_CLEAR_MS);
        return () => clearTimeout(t);
    }, [copied]);

    async function handleConfirm() {
        if (!acknowledged && hasMulti) return;
        setSubmitting(true);
        setError(null);
        try {
            const { tempPassword: tp } = await adminResetPasswordV2(investigationUuid, target.uuid);
            setTempPassword(tp);
            setStage('reveal');
        } catch (e) {
            const msg = e?.response?.data?.errorMessage || e?.message || 'Unknown error';
            setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
            setStage('error');
        } finally {
            setSubmitting(false);
        }
    }

    async function handleCopy() {
        if (!tempPassword) return;
        try {
            await navigator.clipboard.writeText(tempPassword);
            setCopied(true);
        } catch (_) { /* ignore — admin can still read it off-screen */ }
    }

    function handleDone() {
        // Clear local state before unmounting; the temp password is gone after this.
        setTempPassword(null);
        onClose?.();
    }

    if (stage === 'reveal') {
        // Prefer the target's real name when we have it (the user already completed
        // registration, so name + surnames are populated). Fall back to email otherwise.
        const displayName = (target.name && target.surnames)
            ? `${target.name} ${target.surnames}`
            : (target.name || target.email);
        return (
            <Stack spacing={2}>
                <Alert severity="success">
                    <Translate id="investigation.share.reset_password_success" />
                </Alert>
                <Typography variant="body1">
                    <Translate id="investigation.share.reset_password_share_intro" />{' '}
                    <strong>{displayName}</strong>.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        fullWidth
                        value={tempPassword ?? ''}
                        InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                        autoComplete="off"
                        spellCheck={false}
                    />
                    <IconButton aria-label="copy" onClick={handleCopy} title="Copy to clipboard">
                        <ContentCopyIcon />
                    </IconButton>
                </Box>
                {copied && (
                    <Typography variant="caption" color="warning.main">
                        <Translate id="investigation.share.reset_password_copied_warning" />
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                    <Translate id="investigation.share.reset_password_steps_header" />
                </Typography>
                <Box component="ol" sx={{ pl: 3, m: 0 }}>
                    <Typography component="li" variant="body2">
                        <Translate id="investigation.share.reset_password_step_login" />
                    </Typography>
                    <Typography component="li" variant="body2">
                        <Translate id="investigation.share.reset_password_step_change" />
                    </Typography>
                    <Typography component="li" variant="body2">
                        <Translate id="investigation.share.reset_password_step_data" />
                    </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    <Translate id="investigation.share.reset_password_one_shot_notice" />
                </Typography>
                <Button variant="contained" onClick={handleDone} data-testid="reset-password-done">
                    <Translate id="general.done" />
                </Button>
            </Stack>
        );
    }

    if (stage === 'error') {
        return (
            <Stack spacing={2}>
                <Alert severity="error">{error}</Alert>
                <Button variant="outlined" onClick={onClose}><Translate id="general.close" /></Button>
            </Stack>
        );
    }

    // confirm stage
    return (
        <Stack spacing={2}>
            <Typography variant="body1">
                <Translate id="investigation.share.reset_password_confirmation" />{' '}
                <strong>{target.email}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary">
                <Translate id="investigation.share.reset_password_consequences" />
            </Typography>
            {/* Show, up front, what the reset user will have to do — so the admin can
                relay it in person if the notification email never reaches them. The
                temp password itself only appears after confirming (reveal stage). */}
            <Typography variant="body2" color="text.secondary">
                <Translate id="investigation.share.reset_password_steps_header" />
            </Typography>
            <Box component="ol" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body2" color="text.secondary">
                    <Translate id="investigation.share.reset_password_step_login" />
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                    <Translate id="investigation.share.reset_password_step_change" />
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                    <Translate id="investigation.share.reset_password_step_data" />
                </Typography>
            </Box>
            {hasMulti && (
                <>
                    <Alert severity="warning">
                        <Translate id="investigation.share.reset_password_multi_investigation_warning" />
                    </Alert>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={acknowledged}
                                onChange={(e) => setAcknowledged(e.target.checked)}
                                data-testid="reset-multi-ack"
                            />
                        }
                        label={<Translate id="investigation.share.reset_password_multi_investigation_ack" />}
                    />
                </>
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={onClose} disabled={submitting}>
                    <Translate id="general.cancel" />
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={handleConfirm}
                    disabled={submitting || (hasMulti && !acknowledged)}
                    data-testid="reset-password-confirm"
                    startIcon={submitting ? <CircularProgress size={16} /> : null}
                >
                    <Translate id="investigation.share.reset_password_confirm_button" />
                </Button>
            </Stack>
        </Stack>
    );
}
